<template>
  <div class="p-4 lg:p-8">
    <div class="surface-card p-4 shadow-sm border border-iron rounded-lg">

      <div class="flex justify-between items-center mb-6">
        <div>
          <h2 class="text-2xl font-bold text-secondary">Gestão de Alunos</h2>
          <p class="text-sm text-gray-500">Visualize, edite ou cadastre novos estudantes.</p>
        </div>
        <Button label="Novo Aluno" icon="pi pi-plus" @click="openNew" class="bg-purple-suit-forced border-none px-4" />
      </div>

      <DataTable :value="students" :loading="loading" dataKey="id" :paginator="true" :rows="10"
        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
        responsiveLayout="stack" breakpoint="960px" class="p-datatable-sm shadow-sm border border-iron rounded">
        <template #empty> Nenhum aluno encontrado. </template>

        <Column field="name" header="Nome" sortable />

        <Column field="status" header="Status" class="text-center">
          <template #body="slotProps">
            <Tag v-if="slotProps.data.isActive()" severity="success" value="Ativo"></Tag>
            <Tag v-else severity="contrast" value="Inativo"></Tag>
          </template>
        </Column>

        <Column field="birth_day" header="Aniversário" class="text-center">
          <template #body="slotProps">
            {{ slotProps.data.getBirthDate() }}
          </template>
        </Column>

        <Column field="payment_day" header="Dia Pagto." class="text-center">
          <template #body="slotProps">
            <Tag :value="'Dia ' + slotProps.data.payment_day" severity="info" />
          </template>
        </Column>

        <Column header="Horários">
          <template #body="slotProps">
            <div class="flex flex-wrap gap-1">
              <Tag v-for="(h, i) in parseClassTime(slotProps.data.class_time)" :key="i"
                :value="`${h.day.substring(0, 3)} ${h.start}`" class="bg-gray-100 text-gray-700 border border-iron" />
            </div>
          </template>
        </Column>

        <Column header="Ações">
          <template #body="slotProps">
            <Button icon="pi pi-pencil" class="p-button-text p-button-secondary" @click="editStudent(slotProps.data)" />
            <Button icon="pi pi-trash" class="p-button-text p-button-danger" @click="confirmDelete(slotProps.data)" />
            <Button icon="pi pi-file" class="p-button-text p-button-info" @click="generateContract(slotProps.data)" />
            <Button icon="pi pi-key" class="p-button-text p-button-info" @click="grantAccess(slotProps.data)" />

            <Button icon="pi pi-folder-open" class="p-button-text p-button-success" @click="openMaterialsManager(slotProps.data)" v-tooltip="'Materiais Liberados'" />
          </template>
        </Column>
      </DataTable>
    </div>

    <Dialog v-model:visible="studentDialog" :style="{ width: '850px' }" header="Detalhes do Aluno" :modal="true"
      class="p-fluid" :closeOnEscape="true">
      <StudentForm :studentData="selectedStudent" @save="onSave" @cancel="studentDialog = false" />
    </Dialog>

    <ConfirmDialog></ConfirmDialog>

    <ContractGenerationView v-if="viewingContract" :student="selectedStudent" @back="viewingContract = false" />

    <Dialog v-model:visible="accessDialog" :style="{ width: '450px' }" header="Gestão de Acesso do Aluno" :modal="true"
      class="p-fluid" :closeOnEscape="true">
      <div v-if="!selectedStudent?.user" class="flex flex-col gap-4">
        <div class="flex items-start gap-3 bg-blue-50 p-3 rounded border border-blue-200">
          <i class="pi pi-info-circle text-blue-600 mt-0.5" style="font-size: 1.2rem"></i>
          <p class="text-sm text-blue-800 m-0">
            Este aluno ainda não possui acesso aos materiais exclusivos. Ao gerar o acesso, uma senha provisória de 6 caracteres será
            gerada e enviada automaticamente para o e-mail informado abaixo.
          </p>
        </div>

        <div class="field">
          <label for="accessEmail" class="font-semibold text-sm text-secondary mb-1 block">E-mail de Acesso</label>
          <InputText id="accessEmail" v-model="accessEmail" placeholder="exemplo@email.com" class="w-full" />
        </div>

        <div class="flex justify-end gap-2 mt-4">
          <Button label="Cancelar" class="p-button-text p-button-secondary" @click="accessDialog = false"
            :disabled="submittingAccess" />
          <Button label="Gerar Acesso de Aluno" icon="pi pi-key" class="bg-purple-suit-forced border-none"
            @click="handleCreateAccess()" :loading="submittingAccess" />
        </div>
      </div>

      <div v-else class="flex flex-col gap-4">
        <div class="flex items-start gap-3 bg-gray-50 p-3 rounded border border-iron mb-2">
          <i class="pi pi-user text-gray-600 mt-0.5" style="font-size: 1.2rem"></i>
          <div>
            <p class="text-sm font-semibold text-secondary m-0">Acesso Ativo</p>
            <p class="text-xs text-gray-500 m-0">Vinculado ao e-mail: <span class="font-mono">{{
              selectedStudent.user_email
                }}</span></p>
          </div>
        </div>

        <div class="flex flex-col gap-3 mt-2">
          <Button label="Enviar Nova Senha Provisória" icon="pi pi-refresh"
            class="p-button-outlined p-button-info text-left justify-start" @click="handleResetPassword"
            :loading="submittingAccess" />

          <Button v-if="selectedStudent.allowed" label="Remover Acesso do Aluno" icon="pi pi-trash"
            class="p-button-outlined p-button-danger text-left justify-start" @click="handleRevokeAccess"
            :loading="submittingAccess" />
          <Button v-if="!selectedStudent.allowed" label="Adicionar Acesso do Aluno" icon="pi pi-check"
            class="p-button-outlined p-button-danger text-left justify-start" @click="handleGrantAccess"
            :loading="submittingAccess" />
        </div>

        <div class="flex justify-end mt-4">
          <Button label="Fechar" class="p-button-text p-button-secondary" @click="accessDialog = false" />
        </div>
      </div>
    </Dialog>

    <Dialog v-model:visible="materialsDialog" :style="{ width: '550px' }" header="Materiais Exclusivos do Aluno" :modal="true" class="p-fluid" :closeOnEscape="true">
      <div class="flex flex-col gap-4">
        <div class="bg-gray-50 p-3 rounded border border-iron mb-2">
          <p class="text-sm font-semibold text-secondary m-0">Aluno: <span class="text-purple-suit font-bold">{{ selectedStudent?.name }}</span></p>
          <p class="text-xs text-gray-500 m-0">Selecione os materiais que este estudante terá permissão para visualizar e baixar.</p>
        </div>

        <div class="field">
          <label class="font-semibold text-sm text-secondary mb-2 block">Selecione os Materiais</label>
          <MultiSelect 
            v-model="selectedMaterials" 
            :options="allMaterials" 
            optionLabel="title" 
            optionValue="id"
            placeholder="Nenhum material liberado" 
            :filter="true" 
            filterPlaceholder="Buscar material..."
            display="chip" 
            class="w-full"
            :loading="loadingMaterials"
          >
            <template #empty>
              Nenhum material cadastrado no sistema.
            </template>
          </MultiSelect>
        </div>

        <div class="flex justify-end gap-2 mt-4">
          <Button label="Cancelar" class="p-button-text p-button-secondary" @click="materialsDialog = false" :disabled="submittingMaterials" />
          <Button label="Salvar Permissões" icon="pi pi-check" class="bg-purple-suit-forced border-none" @click="handleSaveMaterials" :loading="submittingMaterials" />
        </div>
      </div>
    </Dialog>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Button from 'primevue/button';
import Tag from 'primevue/tag';
import Dialog from 'primevue/dialog';
import ConfirmDialog from 'primevue/confirmdialog';
import { useConfirm } from "primevue/useconfirm";
import StudentForm from './StudentFormView.vue';
import StudentService from '@/services/StudentService';
import { showToast } from '@/composables/toast';
import { useToast } from 'primevue';
import ContractGenerationView from './ContractGenerationView.vue';
import InputText from 'primevue/inputtext';
import MultiSelect from 'primevue/multiselect';
import MaterialService from '@/services/MaterialService';
import { translate } from '@/composables/translate.js';

const toast = useToast();
const confirm = useConfirm();
const students = ref([]);
const loading = ref(false);
const studentDialog = ref(false);
const selectedStudent = ref(null);
const accessDialog = ref(false);
const accessEmail = ref('');
const submittingAccess = ref(false);

const materialsDialog = ref(false);
const loadingMaterials = ref(false);
const submittingMaterials = ref(false);
const allMaterials = ref([]);
const selectedMaterials = ref([]);

onMounted(() => {
  loadStudents();
});

const loadStudents = async () => {
  loading.value = true;
  try {
    const response = await StudentService.get();
    students.value = response;
  } finally {
    loading.value = false;
  }
};

const openNew = () => {
  selectedStudent.value = null;
  studentDialog.value = true;
};

const editStudent = (student) => {
  selectedStudent.value = { ...student };
  studentDialog.value = true;
};

const onSave = () => {
  loadStudents();
};

const parseClassTime = (json) => {
  try {
    return typeof json === 'string' ? JSON.parse(json) : json;
  } catch (e) {
    return [];
  }
};

async function deleteStudent(student) {
  const response = await StudentService.delete(student.id);

  if (!response.success()) {
    showToast(toast, response.getMessage(), 'error');
    return;
  }

  loadStudents();
}

const confirmDelete = (student) => {
  confirm.require({
    message: `Tem certeza que deseja excluir ${student.name}?`,
    header: 'Confirmação',
    icon: 'pi pi-exclamation-triangle',
    acceptLabel: 'Sim, excluir',
    rejectLabel: 'Cancelar',
    acceptClass: 'p-button-danger',
    accept: async () => {
      deleteStudent(student);
    }
  });
};

const viewingContract = ref(false);
const generateContract = (student) => {
  selectedStudent.value = student;
  viewingContract.value = true;
};

const grantAccess = (student) => {
  selectedStudent.value = student;

  accessEmail.value = student.user ? student.user.email : (student.email || '');

  accessDialog.value = true;
};

/**
 * Tries to create an User to this Student, allowing access to system.
 * @param performVinculation - to link Stundet and User (event if email alread belongs to an User)
 */
async function handleCreateAccess(performVinculation = false) {
  if (!accessEmail.value.trim()) {
    showToast(toast, 'Por favor, informe um e-mail válido.', 'error');
    return;
  }

  submittingAccess.value = true;
  const response = await StudentService.grantAccessToStudent(selectedStudent.value.id, accessEmail.value.trim(), performVinculation);

  if (response.success()) {
    if (response.getContent().TYPE != undefined && response.getContent().TYPE == 'EMAIL_IN_USE') {
      confirm.require({
        message: `Já existe um usuário com esse email, deseja vincular esse estudante com o usuário?`,
        header: 'Confirmar vinculação',
        icon: 'pi pi-refresh',
        acceptLabel: 'Sim, prosseguir',
        rejectLabel: 'Cancelar',
        acceptClass: 'p-button-info',
        accept: async () => {
          handleCreateAccess(true);
        }
      });

    } else {
      showToast(toast, 'Acesso criado e credenciais enviadas por e-mail com sucesso!', 'success');
      loadStudents();
      accessDialog.value = false;
    }


  } else {
    showToast(toast, response.getMessage(), 'error');
  }

  submittingAccess.value = false;
}

const handleResetPassword = async () => {
  confirm.require({
    message: `Tem certeza que deseja resetar a senha de ${selectedStudent.value.name}? Uma nova senha provisória será gerada e enviada para o e-mail cadastrado.`,
    header: 'Confirmar Reset de Senha',
    icon: 'pi pi-refresh',
    acceptLabel: 'Sim, resetar e enviar',
    rejectLabel: 'Cancelar',
    acceptClass: 'p-button-info',
    accept: async () => {
      submittingAccess.value = true;
      try {
        const response = await StudentService.resetAccessPassword(selectedStudent.value.id);
        if (response.success()) {
          showToast(toast, 'Nova senha provisória enviada por e-mail com sucesso!', 'success');
          accessDialog.value = false;
        } else {
          showToast(toast, response.getMessage(), 'error');
        }
      } catch (error) {
        showToast(toast, 'Erro ao redefinir senha do aluno.', 'error');
      } finally {
        submittingAccess.value = false;
      }
    }
  });
};

const handleRevokeAccess = async () => {
  confirm.require({
    message: `Atenção: Tem certeza que deseja remover completamente o acesso de ${selectedStudent.value.name}? O usuário não conseguirá mais acessr aos materiais exclusivos.`,
    header: 'Revogar Acesso do Aluno',
    icon: 'pi pi-exclamation-triangle',
    acceptLabel: 'Sim, remover acesso',
    rejectLabel: 'Cancelar',
    acceptClass: 'p-button-danger',
    accept: async () => {
      submittingAccess.value = true;
      const response = await StudentService.revokeAccess(selectedStudent.value.id);

      if (response.success()) {
        showToast(toast, 'Acesso do aluno aos materiais exclusivos foi exluido', 'success');
        accessDialog.value = false;
        loadStudents();

      } else {
        showToast(toast, response.getMessage(), 'error');
      }

      submittingAccess.value = false;
    }
  });
};

const handleGrantAccess = async () => {
  confirm.require({
    message: `Atenção: Tem certeza que deseja adicionar o acesso de ${selectedStudent.value.name}? O usuário terá acesso aos materiais exclusivos.`,
    header: 'Dar Acesso do Aluno',
    icon: 'pi pi-exclamation-triangle',
    acceptLabel: 'Sim, dar acesso',
    rejectLabel: 'Cancelar',
    acceptClass: 'p-button-danger',
    accept: async () => {
      submittingAccess.value = true;
      const response = await StudentService.grantAccessToStudent(selectedStudent.value.id, selectedStudent.value.user_email);

      if (response.success()) {
        showToast(toast, 'Acesso do aluno aos materiais exclusivos foi exluido', 'success');
        accessDialog.value = false;
        loadStudents();

      } else {
        showToast(toast, response.getMessage(), 'error');
      }

      submittingAccess.value = false;
    }
  });
};

/**
 * Abre o gerenciador de mídias do aluno, buscando a lista global e os acessos atuais dele
 */
const openMaterialsManager = async (student) => {
  selectedStudent.value = student;
  selectedMaterials.value = [];
  materialsDialog.value = true;
  loadingMaterials.value = true;

  let avaliableMaterials = [];

  const allGroups = await MaterialService.getGroups() || [];

  for (let group of allGroups) {
    avaliableMaterials.push({
      id: group.value, 
      title: translate(group.label, 'ucfirst')
    });
  }

  allMaterials.value = avaliableMaterials;
  const currentPermissions = await StudentService.getMaterialsGroup(student.id);

  if (currentPermissions && Array.isArray(currentPermissions)) {
    selectedMaterials.value = currentPermissions.map(m => m.value);
  }

  loadingMaterials.value = false;
};

/**
 * Envia o lote de IDs selecionados para o Back-end sincronizar na tabela pivô
 */
const handleSaveMaterials = async () => {
  submittingMaterials.value = true;
  
  const response = await StudentService.syncMaterialsPermissions(selectedStudent.value.id, selectedMaterials.value);

  if (response.success()) {
    showToast(toast, 'Permissões de materiais atualizadas com sucesso!', 'success');
    materialsDialog.value = false;
    loadStudents();
  } else {
    showToast(toast, response.getMessage(), 'error');
  }

  submittingMaterials.value = false;
};
</script>